<template>
  <div
    class="custom-select"
    :class="{ open: isOpen, disabled }"
    @keydown.stop.prevent="onKeydown"
    @blur="onBlur"
  >
    <button
      ref="trigger"
      class="trigger"
      type="button"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-controls="listboxId"
      :disabled="disabled"
      @click="toggle()"
    >
      <span class="label">{{ selectedLabel || placeholder }}</span>
      <span class="chevron" aria-hidden="true">▾</span>
    </button>

    <ul
      v-if="isOpen"
      ref="menu"
      class="menu"
      :class="{ 'drop-up': dropUp }"
      role="listbox"
      :id="listboxId"
      :aria-activedescendant="activeOptionId"
      :aria-multiselectable="multiple ? true : null"
      @mousedown.prevent
    >
      <li
        v-for="(opt, i) in normalizedOptions"
        :key="opt.value"
        class="option"
        role="option"
        :id="optionId(i)"
        :aria-selected="isSelected(i) ? 'true' : 'false'"
        :class="{
          active: i === activeIndex,
          selected: isSelected(i),
          disabled: !!opt.disabled,
        }"
        @mouseenter="setActive(i)"
        @click="!opt.disabled && choose(i)"
      >
        <span class="option-label">{{ opt.label }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
  /**
   * CustomSelect
   * Props:
   *  - modelValue: String|Number (selected value)
   *  - options: Array<{ value, label, disabled? }>
   *  - placeholder: String
   *  - disabled: Boolean
   *
   * Emits:
   *  - update:modelValue
   *  - change (newValue)
   */
  export default {
    name: 'CustomSelect',
    props: {
      modelValue: [String, Number, Array],
      options: {
        type: Array,
        required: true,
      },
      placeholder: {
        type: String,
        default: 'Select…',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      idPrefix: {
        type: String,
        default: 'cds',
      },
      multiple: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['update:modelValue', 'change'],
    data() {
      return {
        isOpen: false,
        activeIndex: -1,
        typeahead: '',
        typeaheadTimer: null,
        dropUp: false,
      };
    },
    computed: {
      normalizedOptions() {
        // Ensure each option has value/label
        return this.options.map((o) => ({
          value: o?.value ?? '',
          label: o?.label ?? String(o?.value ?? ''),
          disabled: !!o?.disabled,
        }));
      },
      selectedIndex() {
        return this.normalizedOptions.findIndex(
          (o) => o.value === this.modelValue
        );
      },
      selectedLabel() {
        if (this.multiple) {
          const arr = Array.isArray(this.modelValue) ? this.modelValue : [];
          const labels = this.normalizedOptions
            .filter((o) => arr.includes(o.value))
            .map((o) => o.label);
          return labels.join(', ');
        } else {
          const i = this.selectedIndex;
          return i >= 0 ? this.normalizedOptions[i].label : '';
        }
      },
      selectedSet() {
        return this.multiple && Array.isArray(this.modelValue)
          ? new Set(this.modelValue)
          : new Set();
      },
      listboxId() {
        return `${this.idPrefix}-listbox`;
      },
      activeOptionId() {
        return this.activeIndex >= 0 ? this.optionId(this.activeIndex) : null;
      },
    },
    methods: {
      optionId(i) {
        return `${this.idPrefix}-opt-${i}`;
      },
      isSelected(i) {
        const val = this.normalizedOptions[i]?.value;
        return this.multiple
          ? this.selectedSet.has(val)
          : val === this.modelValue;
      },
      positionMenu() {
        const trigger = this.$refs.trigger;
        const menu = this.$refs.menu;
        if (!trigger || !menu) return;

        const rect = trigger.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;

        const spaceBelow = Math.max(0, vh - rect.bottom);
        const spaceAbove = Math.max(0, rect.top);

        // let the menu measure its natural height first
        const prevMax = menu.style.maxHeight;
        menu.style.maxHeight = '';
        const naturalHeight = menu.scrollHeight;

        const shouldDropUp =
          naturalHeight > spaceBelow && spaceAbove > spaceBelow;
        this.dropUp = shouldDropUp;

        // cap height to fit in available space
        const allowed = Math.min(
          naturalHeight,
          shouldDropUp ? spaceAbove : spaceBelow,
          vh * 0.6
        );
        menu.style.maxHeight = `${Math.max(120, Math.floor(allowed))}px`;

        if (!menu.style.maxHeight) menu.style.maxHeight = prevMax;
      },
      open() {
        if (this.disabled || this.isOpen) return;
        this.isOpen = true;
        // Focus current selection or first enabled
        this.$nextTick(() => {
          if (!this.multiple) {
            this.activeIndex =
              this.selectedIndex >= 0
                ? this.selectedIndex
                : this.firstEnabledIndex();
          } else {
            // focus first enabled if multi
            this.activeIndex = this.firstEnabledIndex();
          }
          // keep focus on trigger for a11y; mouse users can still interact
          this.$refs.trigger?.focus?.();
          this.positionMenu();
          window.addEventListener('resize', this.positionMenu, {
            passive: true,
          });
          window.addEventListener('scroll', this.positionMenu, {
            passive: true,
            capture: true,
          });

          document.addEventListener('click', this.handleOutside, {
            capture: true,
          });
        });
      },
      close() {
        if (!this.isOpen) return;
        this.isOpen = false;
        window.removeEventListener('resize', this.positionMenu, {
          passive: true,
        });
        window.removeEventListener('scroll', this.positionMenu, {
          passive: true,
          capture: true,
        });
        document.removeEventListener('click', this.handleOutside, {
          capture: true,
        });
      },
      toggle() {
        this.isOpen ? this.close() : this.open();
      },
      choose(i) {
        if (i < 0) return;
        const opt = this.normalizedOptions[i];
        if (!opt || opt.disabled) return;
        if (this.multiple) {
          const current = Array.isArray(this.modelValue)
            ? [...this.modelValue]
            : [];
          const idx = current.indexOf(opt.value);
          if (idx === -1) current.push(opt.value);
          else current.splice(idx, 1);
          this.$emit('update:modelValue', current);
          this.$emit('change', current);
        } else {
          if (opt.value !== this.modelValue) {
            this.$emit('update:modelValue', opt.value);
            this.$emit('change', opt.value);
          }
          this.close();
        }
      },
      setActive(i) {
        if (i < 0) return;
        this.activeIndex = i;
      },
      move(delta) {
        if (!this.isOpen) this.open();
        const len = this.normalizedOptions.length;
        if (!len) return;
        let i = this.activeIndex;
        // Start from current or selected or first enabled
        if (i < 0)
          i =
            this.selectedIndex >= 0
              ? this.selectedIndex
              : this.firstEnabledIndex();
        for (let step = 0; step < len; step++) {
          i = (i + delta + len) % len;
          if (!this.normalizedOptions[i].disabled) {
            this.activeIndex = i;
            break;
          }
        }
      },
      firstEnabledIndex() {
        return this.normalizedOptions.findIndex((o) => !o.disabled);
      },
      onKeydown(e) {
        const k = e.key;
        switch (k) {
          case 'ArrowDown':
            this.move(1);
            break;
          case 'ArrowUp':
            this.move(-1);
            break;
          case 'Home':
            this.activeIndex = this.firstEnabledIndex();
            break;
          case 'End': {
            const last = [...this.normalizedOptions]
              .reverse()
              .findIndex((o) => !o.disabled);
            if (last !== -1)
              this.activeIndex = this.normalizedOptions.length - 1 - last;
            break;
          }
          case 'Enter':
          case ' ':
            if (!this.isOpen) this.open();
            else this.choose(this.activeIndex);
            break;
          case 'Escape':
            this.close();
            break;
          default:
            this.handleTypeahead(k);
        }
      },
      onBlur() {
        // Let clicks inside menu land via mousedown.prevent; just close on true blur
        // (Kept minimal; outside click handler also closes)
      },
      handleOutside(ev) {
        if (!this.$el.contains(ev.target)) this.close();
      },
      handleTypeahead(char) {
        if (char.length !== 1) return;
        const c = char.toLowerCase();
        if (!/[a-z0-9\s\-_.]/.test(c)) return;

        clearTimeout(this.typeaheadTimer);
        this.typeahead += c;

        const start = Math.max(this.activeIndex, 0);
        const opts = this.normalizedOptions;

        const matchFrom = (s) =>
          opts.findIndex(
            (o, idx) =>
              idx >= s &&
              !o.disabled &&
              o.label.toLowerCase().startsWith(this.typeahead)
          );

        let idx = matchFrom(start);
        if (idx === -1) idx = matchFrom(0);
        if (idx !== -1) this.activeIndex = idx;

        this.typeaheadTimer = setTimeout(() => (this.typeahead = ''), 600);
      },
    },
    beforeUnmount() {
      document.removeEventListener('click', this.handleOutside, {
        capture: true,
      });
      clearTimeout(this.typeaheadTimer);
    },
  };
</script>

<style scoped>
  * {
    user-select: none;
  }

  .custom-select {
    position: relative;
    cursor: pointer;
    min-width: 0;
    max-width: 100%;
  }

  .trigger {
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    box-sizing: border-box;
    border: var(--border);
    border-radius: var(--border-radius);
    background: var(--surface);
    padding: 0 12px;
    width: 100%;
    height: 36px;
    overflow: hidden;
    color: var(--text);
    font-size: 16px;
  }

  .open .trigger {
    border-color: var(--accent);
    background: var(--surface-light);
  }

  .trigger:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .label {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .chevron {
    flex: 0 0 auto;
    margin-left: 8px;
  }

  /* Dropdown panel */
  .menu {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 1000;
    backdrop-filter: blur(3px);
    margin: 0;
    box-shadow: var(--shadow);
    border-radius: var(--border-radius);
    background: var(--surface);
    padding: 6px 0;
    width: 100%;
    max-height: 60vh;
    overflow-y: auto;
    scrollbar-color: var(--text) var(--surface);
    color: var(--text);
    list-style: none;
  }

  .menu.drop-up {
    top: auto;
    bottom: calc(100% + 6px);
  }

  /* Options */
  .option {
    cursor: pointer;
    padding: 10px 12px;
    line-height: 1.2;
  }

  .option + .option {
    border-top: 1px dashed rgba(255, 255, 255, 0.1);
  }

  .option:hover,
  .option.active {
    background: var(--surface-semilight);
  }

  .option.selected {
    background: var(--surface-light);
    font-weight: 600;
  }

  .option.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
